import * as Sentry from '@sentry/react';
import type { QueryClient } from '@tanstack/react-query';

import {
  getPeopleCurrentOptions,
  getTeamOptions,
} from 'client/@tanstack/react-query.gen';
import type { PersonV1CurrentResponse } from 'client/types.gen';
import { isClerkOfflineError } from 'helpers/clerkErrors';
import { getPersonRole } from 'helpers/person';

/**
 * The signed-in person from `GET /people/current`, or `undefined` when there
 * is no Clerk session. Reads through the query cache, so calling it again in
 * the same load does not refetch.
 */
export const resolveCurrentUser = async (
  queryClient: QueryClient,
): Promise<PersonV1CurrentResponse | undefined> => {
  // @ts-ignore
  const session = window.Clerk?.session;

  try {
    const token = await session?.getToken();

    if (token) {
      return await queryClient.ensureQueryData(getPeopleCurrentOptions());
    }
  } catch (error) {
    // Clerk v6 getToken throws when offline where v5 returned null; a
    // network blip should not end the session
    if (!isClerkOfflineError(error)) {
      Sentry.captureException(error);
      await session?.end?.();
      queryClient.clear();
    }
  }

  return undefined;
};

/**
 * The signed-in person and their role for `teamId`, resolved together so a
 * caller never pairs a user with a role from another load.
 */
export const resolveSession = async (
  queryClient: QueryClient,
  teamId: string | undefined,
) => {
  const user = await resolveCurrentUser(queryClient);

  if (!user) {
    return { role: undefined, user: undefined };
  }

  let currentTeam;
  if (teamId) {
    try {
      currentTeam = await queryClient.ensureQueryData(
        getTeamOptions({ path: { team_slug: teamId } }),
      );
    } catch {
      // a member of an archived team gets 401 here; the team route below
      // redirects them, so resolve the role without a current team
      currentTeam = undefined;
    }
  }

  const teamSlug = currentTeam ? currentTeam.slug : undefined;
  const role = getPersonRole(user, teamSlug);

  if (role || !teamSlug) {
    return { role, user };
  }

  // The cached person may predate this team: one they just created, an invite
  // they just accepted, or an advisor adding them. Refetch once before
  // treating them as having no role on it.
  const freshUser = await queryClient.fetchQuery({
    ...getPeopleCurrentOptions(),
    staleTime: 0,
  });

  return { role: getPersonRole(freshUser, teamSlug), user: freshUser };
};
